import {
  formatDate,
  PHONE_NUMBER_MAX_LENGTH,
  PHONE_NUMBER_MIN_LENGTH,
  transformFloatToEnglishLocale,
} from '@backoffice-frontend/common'
import { isAfter, isBefore, isDate, isValid } from 'date-fns'
import type { FieldValidator } from 'final-form'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

export const useValidators = () => {
  const { t } = useTranslation()
  return {
    required: required(t),
    requiredDate: requiredDate(t),
    exactLengthWithProperties: exactLengthWithProperties(t),
    lengthRangeWithProperties: lengthRangeWithProperties(t),
    requiredArrayElement: requiredArrayElement(t),
    lengthRange: lengthRange(t),
    minLength: minLength(t),
    maxLength: maxLength(t),
    minLength3: minLength3(t),
    validateUuidStandardFormat: validateUuidStandardFormat(t),
    validateDateTimeInPast: validateDateTimeInPast(t),
    validateDateTimeInFuture: validateDateTimeInFuture(t),
    validateDateTimeIsBefore: validateDateTimeIsBefore(t),
    isValidDate: isValidDate(t),
    validateDateTime: validateDateTime(t),
    validateDate: validateDate(t),
    validateDateTimeMui: validateDateTimeMui(t),
    validateGeoCoordLat: validateGeoCoordLat(t),
    validateGeoCoordLong: validateGeoCoordLong(t),
    validateLicensePlate: validateLicensePlate(t),
    validatePrice: validatePrice(t),
    validateTime: validateTime(t),
    noWhitespace: noWhitespace(t),
    phoneNumber: phoneNumber(t),
    email: email(t),
    minValue: minValue(t),
    maxValue: maxValue(t),
    integerNumber: integerNumber(t),
    numberRangeWithProperties: numberRangeWithProperties(t),
    number: number(t),
    validateMonthYear: validateMonthYear(t),
    englishNumber: englishNumber(t),
    noSpecialChars: noSpecialChars(t),
    onlyNumbersAndLetters: onlyNumbersAndLetters(t),
    validateVIN: validateVIN(t),
    validateVehicleLabel: validateVehicleLabel(t),
  }
}
export const usePaceValidators = () => {
  const { t } = useTranslation()
  return {
    required: t('commonValidators_requiredField_error'),
    emailPattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: t('commonValidators_invalidEmail_error'),
    },
  }
}

const validateTime =
  (t: TFunction) =>
  (value: Date): string | undefined =>
    isValid(value) ? undefined : t('commonValidators_invalidTimeFormat_error')

// biome-ignore lint/suspicious/noExplicitAny: using `unknown` breaks FieldValidator<T> inference in composeValidators consumers
export const required = (t: TFunction) => (value: any) => {
  if (typeof value === 'boolean' || typeof value === 'number') {
    return undefined
  }
  return value ? undefined : t('commonValidators_requiredField_error')
}

export const requiredDate = (t: TFunction) => (value: Date | null) => {
  // the check in the regular required:  if (typeof value === 'boolean' || typeof value === 'number') is causing date required validation to fail
  // that is why we need this additional requiredDate validator
  return value ? undefined : t('commonValidators_requiredField_error')
}

export const isValidDate = (t: TFunction) => (value: Date | null) => {
  return !value || isValid(value)
    ? undefined
    : t('commonValidators_invalidDate_error')
}

export const requiredArrayElement =
  (t: TFunction) =>
  <T>(value: T[]) =>
    value && value.length > 0
      ? undefined
      : t('commonValidators_requiredField_error')

export const maxLength = (t: TFunction) => (max: number) => (value: string) =>
  value && value.length > max
    ? t('commonValidators_maxLength_error', { length: max })
    : undefined

export const minLength = (t: TFunction) => (min: number) => (value: string) =>
  value && value.length < min
    ? t('commonValidators_minLength_error', { length: min })
    : undefined

export const lengthRangeWithTranslation =
  <T>(min: number, max: number, translation: string) =>
  (value: T) => {
    if (typeof value === 'string') {
      const length = Number(value.length)
      return length < min || length > max ? translation : undefined
    }

    return undefined
  }
export const lengthRange = (t: TFunction) => (min: number, max: number) =>
  lengthRangeWithTranslation(
    min,
    max,
    t('commonValidators_numberRange_error', {
      min,
      max,
    }),
  )
export const lengthRangeWithProperties =
  (t: TFunction) => (min: number, max: number) =>
    lengthRangeWithTranslation(
      min,
      max,
      t('commonValidators_numberRange_error', { min, max }),
    )

export const exactLengthWithProperties =
  (t: TFunction) => (exactLength: number) =>
    lengthRangeWithTranslation(
      exactLength,
      exactLength,
      t('commonValidators_exactCharacterLength_error', { exactLength }),
    )

export const minLength3 = (t: TFunction) => minLength(t)(3)
export const maxLength15 = (t: TFunction) => maxLength(t)(15)

export const number = (t: TFunction) => (value: number | string) => {
  if (!value) {
    return undefined
  }

  const number = transformFloatToEnglishLocale(value)
  return Number.isNaN(Number(number))
    ? t('commonValidators_invalidNumber_error')
    : undefined
}

// NOTE: if the translationKeyOrFunction is set to a function, it's used later,
//       in the TextField component as a way to provide payload to the translation fn t
export const numberRange =
  (min: number, max: number, translation: string, t: TFunction) =>
  (value?: string | number) => {
    const numberValidation = value && number(t)(value)
    if (numberValidation) {
      return numberValidation
    }

    const numberValue = Number(value)
    return !Number.isNaN(numberValue) &&
      (min > numberValue || max < numberValue)
      ? translation
      : undefined
  }

export const numberRangeWithProperties =
  (t: TFunction) => (min: number, max: number) =>
    numberRange(
      min,
      max,
      t('commonValidators_numberRange_error', { min, max }),
      t,
    )

export const integerNumber =
  (t: TFunction) =>
  <T>(value: T) =>
    value && !Number.isInteger(Number(value))
      ? t('commonValidators_noDecimals_error')
      : undefined

const castFloat = <T>(floatString: T) => {
  if (typeof floatString === 'number') return floatString
  if (typeof floatString === 'string')
    return Number.parseFloat(floatString.replace(',', '.'))

  return undefined
}

export const maxValue =
  (t: TFunction) => (max?: string | number) => (value: string | number) => {
    if (!value || !max) {
      return undefined
    }

    const parsedMax = castFloat(max)
    const parsedValue = castFloat(value)
    return value && parsedValue && parsedMax && parsedValue > parsedMax
      ? t('commonValidators_maxNumber_error', { max })
      : undefined
  }

export const minValue =
  <T>(t: TFunction) =>
  (min: string | number) =>
  (value: T) => {
    if (!value || !min) {
      return undefined
    }

    const parsedMin = castFloat(min)
    const parsedValue = castFloat(value)
    return value && parsedValue && parsedMin && parsedValue < parsedMin
      ? t('commonValidators_minNumber_error', { min })
      : undefined
  }

export const positiveValue = <T>(value: T) => {
  const parsedValue = castFloat(value)
  return parsedValue && parsedValue < 0
}

export const email = (t: TFunction) => (value: unknown) =>
  value &&
  typeof value === 'string' &&
  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
    ? t('commonValidators_invalidEmail_error')
    : undefined

export const phoneNumber = (t: TFunction) => (value: string) => {
  if (!value) return undefined

  const isFormattedCorrectly = /^\+[\d]{9,17}$/i.test(value)
  const hasCorrectLength =
    value.length >= PHONE_NUMBER_MIN_LENGTH &&
    value.length <= PHONE_NUMBER_MAX_LENGTH

  return !(isFormattedCorrectly && hasCorrectLength)
    ? t('commonValidators_invalidPhoneNumber_error')
    : undefined
}

export const noWhitespace = (t: TFunction) => (value: string) =>
  /\s/g.test(value) ? t('commonValidators_spacesNotAllowed_error') : undefined

export const validatePrice =
  <T>(t: TFunction) =>
  (value: T) => {
    return value && Number.isNaN(transformFloatToEnglishLocale(value))
      ? t('commonValidators_invalidPrice_error')
      : undefined
  }

export const validateGeoCoordLat = (t: TFunction) => (value: string) =>
  value && !/^([-+]?)([\d]{1,2})(((\.)(\d+)))$/i.test(value)
    ? t('commonValidators_invalidLatitude_error')
    : undefined

export const validateGeoCoordLong = (t: TFunction) => (value: string) =>
  value && !/^(([-+]?)([\d]{1,3})((\.)(\d+))?)$/i.test(value)
    ? t('commonValidators_invalidLongitude_error')
    : undefined

export const validateDate =
  (t: TFunction) =>
  <T>(value: T) => {
    if (!value || isDate(value)) return undefined
    if (typeof value === 'string' || typeof value === 'number')
      return !isValid(new Date(value))
        ? t('commonValidators_invalidDateFormatDay_error')
        : undefined

    return t('commonValidators_invalidDateFormatDay_error')
  }

export const validateDateTime =
  <T>(t: TFunction) =>
  (value: T) => {
    if (!value || isDate(value)) return undefined
    if (typeof value === 'string' || typeof value === 'number')
      return !isValid(new Date(value))
        ? t('commonValidators_invalidDateTimeFormat_error')
        : undefined

    return t('commonValidators_invalidDateTimeFormat_error')
  }

export const validateDateTimeMui =
  (t: TFunction) =>
  <Date>(value: Date) => {
    if (!value || !isDate(value)) return undefined
    if (value instanceof Date)
      return !isValid(new Date(value))
        ? t('commonValidators_invalidDateTimeFormat_error')
        : undefined

    return t('commonValidators_invalidDateTimeFormat_error')
  }

export const validateDateTimeIsBefore =
  <T>(t: TFunction) =>
  (dateToCompare: Date) =>
  (value: T) => {
    let valueDate: Date

    if (typeof value === 'string' || typeof value === 'number') {
      valueDate = new Date(value)
    } else if (isDate(value)) {
      valueDate = value
    } else {
      return undefined
    }

    return isBefore(dateToCompare, valueDate)
      ? t('commonValidators_dateTooEarly_error', {
          date: formatDate(dateToCompare),
        })
      : undefined
  }

export const validateDateTimeInFuture =
  <T>(t: TFunction) =>
  (value: T) => {
    let valueDate: Date

    if (typeof value === 'string' || typeof value === 'number') {
      valueDate = new Date(value)
    } else if (isDate(value)) {
      valueDate = value
    } else {
      return undefined
    }

    return isBefore(valueDate, new Date())
      ? t('commonValidators_dateNotFuture_error')
      : undefined
  }

export const validateDateTimeInPast =
  (t: TFunction) => (value: Date | string | number) => {
    if (!value) return undefined

    const valueDate = new Date(value)

    return isAfter(valueDate, new Date())
      ? t('commonValidators_dateNotPast_error')
      : undefined
  }

export const validateUuidStandardFormat = (t: TFunction) => (value: string) => {
  if (value.length !== 36) {
    return t('commonValidators_invalidUuid_length_error')
  }
  if (value.split('-').length !== 5 || !/^[a-zA-Z0-9-]+$/.test(value))
    return t('commonValidators_invalidUuid_error')
  return undefined
}

/**
 * Validates a license plate format and length.
 * Maximum length: 15 characters
 * Valid characters: A-Z, 0-9, and hyphens (-)
 *
 * @param t - Translation function for error messages
 * @returns A validator function that takes a license plate string and returns an error message or undefined
 */
export const validateLicensePlate = (t: TFunction) => (value: string) => {
  if (value.length > 15)
    return t('commonValidators_invalidLicensePlate_length_error')

  if (!/^[A-Z0-9-]{1,15}$/.test(value)) {
    return t('commonValidators_invalidLicensePlate_format_error')
  }
  return undefined
}

/**
 *
 * Validates an international vehicle identification number (VIN).
 * Exactly 17 characters, valid characters: 0-9, A-H, J-N, P-Z (excludes I, O, Q)
 *
 * @param t - Translation function for error messages
 * @param value - String Form Field value
 * @returns - Returns a translated error message if validation fails, or undefined if valid
 *
 */
export const validateVIN = (t: TFunction) => (value: string) => {
  if (value.length !== 17) return t('commonValidators_invalidVIN_length_error')

  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(value)) {
    return t('commonValidators_invalidVIN_format_error')
  }
  return undefined
}

/**
 * Validates a vehicle label format and length.
 * Maximum length: 5 characters
 * Valid characters: 0-9 (numbers only)
 *
 * @param t - Translation function for error messages
 * @returns A validator function that takes a vehicle label string and returns an error message or undefined
 */
export const validateVehicleLabel = (t: TFunction) => (value: string) => {
  if (value.length > 5)
    return t('commonValidators_invalidVehicleLabel_length_error')

  if (!/^[0-9]{1,5}$/.test(value)) {
    return t('commonValidators_invalidVehicleLabel_format_error')
  }
  return undefined
}

/*
 *  use to compose multiple validators on one single field
 */
export const composeValidators =
  <T>(validators: (FieldValidator<T> | undefined)[]): FieldValidator<T> =>
  (value, allValues, meta) =>
    validators
      .filter(
        (validator): validator is FieldValidator<T> => validator !== undefined,
      )
      .reduce<string | undefined>(
        (error, validator) => error ?? validator(value, allValues, meta),
        undefined,
      )

export const validateMonthYear =
  (t: TFunction) =>
  (value: string): string | undefined => {
    const regex = /^[0-9]{4}-[0-9]{2}$/
    return regex.test(value)
      ? undefined
      : t('commonValidators_invalidDateFormatMonth_error')
  }

export const englishNumber =
  (t: TFunction) =>
  (value: string | number): string | undefined => {
    if (!value) {
      return undefined
    }
    return Number.isNaN(Number(value))
      ? t('commonValidators_invalidNumber_error')
      : undefined
  }

export const noSpecialChars =
  (t: TFunction) =>
  (value: string): string | undefined => {
    const regex = /[<>":/*~]/
    return regex.test(value)
      ? t('commonValidators_noSpecialCharacters_error')
      : undefined
  }

export const onlyNumbersAndLetters =
  (t: TFunction) =>
  (value: string): string | undefined => {
    const regex = /^[a-zA-Z0-9 ]*$/
    return regex.test(value)
      ? undefined
      : t('commonValidators_onlyLettersAndNumbersAllowed_error')
  }
